import { FC, MouseEvent, MutableRefObject, useRef, useState } from "react";
import styles from "./index.module.scss";
import classNames from "classnames";
import useOutsideClickCallback from "@/hooks/useOutsideClickCallback";
import Trash from "@/assets/images/Trash";
import Minus from "@/assets/images/Minus";
import Plus from "@/assets/images/Plus";

export type RowClickCallback = ((event?: MouseEvent<HTMLDivElement>) => void) | undefined;

interface AmountProps {
  amount: number;
  onIncrement?: () => void;
  onDecrement?: () => void;
  onRemoveAll?: () => void;
  minimum?: number;
  maximum?: number;
  customClickCallbackRef?: MutableRefObject<RowClickCallback>;
  outsideClickElementRef?: MutableRefObject<HTMLDivElement | null>;
}

const Amount: FC<AmountProps> = ({
  amount,
  onIncrement,
  onDecrement,
  onRemoveAll,
  minimum,
  maximum,
  customClickCallbackRef,
  outsideClickElementRef,
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const outsideClickRef = outsideClickElementRef ?? ref;
  useOutsideClickCallback(outsideClickRef, () => setIsOpened(false));

  const canDecrement = (minimum === undefined || amount > minimum) && !!onDecrement;
  const canIncrement = (maximum === undefined || amount < maximum) && !!onIncrement;

  const maybeOpenButtons = () => {
    if (!canDecrement && !canIncrement) return;
    setIsOpened(true);
  };

  const onOpenClick = () => {
    if (!customClickCallbackRef) maybeOpenButtons();
  };

  if (!!customClickCallbackRef) {
    customClickCallbackRef.current = (event) => {
      if (!isOpened) maybeOpenButtons();
      else setIsOpened(!!ref.current?.contains(event?.target as Node));
    };
  }

  const onMinusClick = () => {
    if (canDecrement) onDecrement();
  };

  const onPlusClick = () => {
    if (canIncrement) onIncrement();
  };

  return (
    <div>
      {isOpened ? (
        <div className="row" ref={ref}>
          {onRemoveAll && <Trash onClick={onRemoveAll} className={styles.trash} />}
          <div className={styles.expanded}>
            <div
              className={classNames(
                styles.iconWrapper,
                canDecrement ? styles.enabled : styles.disabled
              )}
              onClick={onMinusClick}
              data-cy="decrement-quantity">
              <Minus />
            </div>
            <p className={classNames("h4", styles.amount)} data-cy="quantity">
              {amount}
            </p>
            <div
              className={classNames(
                styles.iconWrapper,
                canIncrement ? styles.enabled : styles.disabled
              )}
              onClick={onPlusClick}
              data-cy="increment-quantity">
              <Plus />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={classNames(styles.container, "h4")}
          onClick={onOpenClick}
          data-cy="amount-closed">
          {amount}
        </div>
      )}
    </div>
  );
};

export { Amount };
